import { useEffect, useState } from 'react';
import commonStyles from "../../../style/common.module.css";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import { ModuleAssignment } from '../../model/module-assignment';
import { BadgeAssignment } from '../../model/badge-assignment';
import { LoadingButton } from '@mui/lab';
import { useDialog } from '../../../../context/dialog/dialog-provider';
import { DialogType, informationText } from '../../../../context/dialog/dialog-context-type';
import { getNumber } from '../../../../utils/helper/string-helper';
import StudentJournalBadgeContent from './student-journal-badge-content';
import { AssignmentStatus } from '../../enum/assignment-status';
import { useSearchParams } from 'react-router-dom';
import {  ASSIGNMENT_QUERY_PARAMETER_TAB_VALUE_JOURNAL } from '../../utils/assignment-constants';
import { getBadgeTerminology, getModuleCourseLevel, getModuleTerminology, getGeneralCourseLevel } from '../../utils/terminology';
import { CourseLevel } from '../../../-education/enum/course-level';
import { updateSearchParams } from '../../utils/assignment-navigation';
import { getFilter } from '../utils/student-assignment-tools';
import PresentationViewer from '../../../../component/presentation/presentation-viewer';

export default function StudentJournal({
    generalCourseLevel,
    submittedModuleAssignments,
    updateModuleAssignments
}: {
        generalCourseLevel: CourseLevel,
        submittedModuleAssignments: ModuleAssignment[],
        updateModuleAssignments(badgeAssignment: BadgeAssignment): void
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [moduleId, setModuleId] = useState<string>("");
    const [moduleIdInitiated, setModuleIdInitiated] = useState(false);
    const [submittedBadgeAssignment, setSubmittedBadgeAssignment] = useState<BadgeAssignment>({});
    
    const dialog = useDialog();
    const studentCourseLevel = submittedModuleAssignments.length > 0 ? getGeneralCourseLevel(submittedModuleAssignments) : generalCourseLevel;

    useEffect(() => {
        if (moduleIdInitiated === false) {
            const selectedModule = getFilter(submittedModuleAssignments, searchParams).moduleId
            setModuleId(selectedModule ?? "");
            setModuleIdInitiated(true);
        }
    }, [moduleIdInitiated, searchParams, submittedModuleAssignments]);

    useEffect(() => {
        updateSearchParams(searchParams, setSearchParams, submittedBadgeAssignment, ASSIGNMENT_QUERY_PARAMETER_TAB_VALUE_JOURNAL);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submittedBadgeAssignment]);

    const getBadges = () => {
        const module = submittedModuleAssignments.find(_ => _.moduleId === moduleId);
        return module?.badges.filter(_ => _.submissionDate !== undefined || _.evaluatedDate !== undefined) ?? [] as BadgeAssignment[];
    }

    const handleBadgeSelect = (badgeAssignment: BadgeAssignment) => {
        if (badgeAssignment.workItemId !== submittedBadgeAssignment.workItemId) {
            setSubmittedBadgeAssignment(badgeAssignment);
        } else {
            setSubmittedBadgeAssignment({ ...submittedBadgeAssignment, workItemId: undefined });
        }
    }

    const openHelpInformationDialog = () => {
        dialog.openDialog(DialogType.INFORMATION, "Help / Information", [
            informationText(`In the list of <b>
                ${getBadgeTerminology(studentCourseLevel)} submissions</b>, you can see the <b>rating</b> from the evaluator. It will be empty until the evaluator looks at your work and leaves a rating, or a comment.`),
            informationText(`The evaluator can review <b>
                ${getBadgeTerminology(studentCourseLevel)} submission</b> with a <b>comment and no rating</b>, informing you about something. A comment icon will indicate this.`),
            informationText(`After you opened a <b>
                ${getBadgeTerminology(studentCourseLevel)} submission</b> then you can <b>Edit</b> or <b>Undo submission</b> it. To do so, click on the menu in the top right corner of the 
                ${getBadgeTerminology(studentCourseLevel)} submission, there you will find the options. These options are only possible until the 
                ${getBadgeTerminology(studentCourseLevel)} submission is <b>rated</b>.`)
        ]);
    }

    return (
        <div className={commonStyles.pb} >
            {submittedModuleAssignments.length > 0 && <FormControl id={`module-form-control`} sx={{ flex: '1 1 100%', alignSelf: 'flex-start', width: '100%', paddingBottom: '1rem' }}>
                <InputLabel id={`module-label`}>{getModuleTerminology(getModuleCourseLevel(submittedModuleAssignments, submittedBadgeAssignment.badgeId))}</InputLabel>
                <Select
                    labelId={`module-label`}
                    id={`module-select`}
                    value={moduleId ?? ''}
                    label={`${getModuleTerminology(getModuleCourseLevel(submittedModuleAssignments, submittedBadgeAssignment.badgeId))}`}
                    onChange={(e) => {
                        setSubmittedBadgeAssignment({
                            ...submittedBadgeAssignment,
                            moduleId: e.target.value,
                            workItemId: undefined
                        })
                        setModuleId(e.target.value)
                    }}
                >
                    {submittedModuleAssignments.map(_ => {
                        return <MenuItem key={_.moduleId} id={`${_}-menu-item`} value={_.moduleId}>
                            {_.moduleName}
                        </MenuItem>
                    })}
                </Select>
            </FormControl>}
            {submittedModuleAssignments.length === 0 && <div>
                <span>
                    Your <b>Journal</b> is empty, when you have submitted an&nbsp;
                    {getBadgeTerminology(studentCourseLevel)} then you can view, and futher handle, it from here.
                </span>
            </div>}
            {<div className={commonStyles.mb}>
                {getBadges().map(_ => {
                    return (<Accordion key={_.workItemId} expanded={submittedBadgeAssignment.workItemId === _.workItemId} onChange={() => handleBadgeSelect(_)}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ width: '99%', flexShrink: 0, }}>
                                {_.badgeName}
                                {getNumber(_.rating) > 0 && Array.from({ length: getNumber(_.rating) }, () => <StarIcon fontSize="small" key={`${Math.random()}-select-icon`} color="primary" sx={{ float: 'right' }} />)}
                                {_.assignmentStatus === AssignmentStatus.Evaluated_Commented && <ChatBubbleIcon color="primary" sx={{ float: 'right' }} />}
                                {_.assignmentStatus === AssignmentStatus.Dismissed && <CloseIcon color="primary" sx={{ float: 'right' }} />}
                            </Typography>
                        </AccordionSummary>
                        {submittedBadgeAssignment.workItemId === _.workItemId && <AccordionDetails>
                            <StudentJournalBadgeContent
                                initialBadge={submittedBadgeAssignment}
                                updateBadge={(badge) => {
                                    updateModuleAssignments(badge);
                                    setSubmittedBadgeAssignment(badge);
                                }}
                                removeBadge={(badge) => {
                                    badge.workItemId = undefined;
                                    badge.submissionDate = undefined;
                                    badge.evaluatedDate = undefined;
                                    badge.comment = undefined;
                                    badge.workingBadgePath = undefined;
                                    if (getBadges().length === 0) {
                                        setSubmittedBadgeAssignment({});
                                    }
                                    updateModuleAssignments(badge);
                                }}
                                moduleCourseLevel={getModuleCourseLevel(submittedModuleAssignments, submittedBadgeAssignment.badgeId)}
                            />
                            {(_.presentationUrl || _.sessionMapUrl) && (
                                <PresentationViewer
                                    badgeName={_.badgeName ?? ''}
                                    presentationUrl={_.presentationUrl}
                                    sessionMapUrl={_.sessionMapUrl}
                                    showSessionMap={false}
                                />
                            )}
                        </AccordionDetails>}
                    </Accordion>)
                })}
            </div>}
            {submittedModuleAssignments.length > 0 && <div style={{ display: 'flex', justifyContent: 'end' }}>
                <LoadingButton
                    id={`info-button`}
                    loadingPosition="start"
                    startIcon={<HelpOutlineIcon />}
                    variant="outlined"
                    onClick={() => openHelpInformationDialog()}
                >
                    Info / Help
                </LoadingButton>
            </div>}
        </div>
    );
}